import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContactImportAction,
  ContactImportProcessDto,
  ExternalBankAccountImportDto,
  ExternalBusinessUnitImportDto,
  ExternalCompanyImportDto,
  ExternalContactImportDto,
  UpdateContactCompanyFromImportProcessDto,
} from '../../../../../models/ContactImportProcess';
import { makePrioStyles } from '../../../../../theme/utils';
import {
  Button,
  Drawer,
  Input,
  Select,
  Tabs,
} from '@prio365/prio365-react-library';
import { Form, List, Table } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { apiUpdateContactsFromImportProcess } from '../../../api';
import ExternalContactDrawer from './ExternalContactDrawer';

const useStyles = makePrioStyles((theme) => ({
  drawer: {
    zIndex: 999,
    padding: 0,
  },
  formItem: {
    marginBottom: theme.spacing.regular,
    width: '100%',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing.regular,
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing.regular,
    marginBottom: theme.spacing.small,
  },
  inlineTable: {
    marginBottom: theme.spacing.regular,
  },
}));

interface ExternalCompanyDrawerProps {
  visible: boolean;
  onClose: () => void;
  selectedCompany: ExternalCompanyImportDto | null;
  businessUnits: ExternalBusinessUnitImportDto[];
  contacts: ExternalContactImportDto[];
  contactImportProcess: ContactImportProcessDto | null;
  onProcessUpdate: (updatedProcess: ContactImportProcessDto) => void;
}

const contactImportActionOptions = (t: any) => [
  {
    value: ContactImportAction.NotSet,
    label: t('settings:companyImport.drawerActions.notSet'),
  },
  {
    value: ContactImportAction.Skip,
    label: t('settings:companyImport.drawerActions.skip'),
  },
  {
    value: ContactImportAction.UpdateMissing,
    label: t('settings:companyImport.drawerActions.updateMissing'),
  },
  {
    value: ContactImportAction.OverrideExisting,
    label: t('settings:companyImport.drawerActions.overrideExisting'),
  },
  {
    value: ContactImportAction.Create,
    label: t('settings:companyImport.drawerActions.create'),
  },
];

const ExternalCompanyDrawer: React.FC<ExternalCompanyDrawerProps> = ({
  visible,
  onClose,
  selectedCompany,
  businessUnits,
  contacts,
  contactImportProcess,
  onProcessUpdate,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState('company');
  const [form] = Form.useForm();
  const [bankAccounts, setBankAccounts] = useState<
    ExternalBankAccountImportDto[]
  >([]);
  const [selectedContact, setSelectedContact] =
    useState<ExternalContactImportDto | null>(null);
  const [isContactDrawerOpen, setIsContactDrawerOpen] = useState(false);

  useEffect(() => {
    if (selectedCompany) {
      form.setFieldsValue({
        legalName: selectedCompany.legalName,
        displayName: selectedCompany.displayName,
        notes: selectedCompany.notes,
        commercialRegisterNumber: selectedCompany.commercialRegisterNumber,
        commercialRegisterCity: selectedCompany.commercialRegisterCity,
        legalForm: selectedCompany.legalForm,
        vatNumber: selectedCompany.vatNumber,
        action: selectedCompany.action,
      });
      setBankAccounts(selectedCompany.externalBankAccountImports || []);
    }
  }, [selectedCompany, form]);

  const handleFinish = async (values: any) => {
    if (!selectedCompany || !contactImportProcess) return;

    const updateDto: UpdateContactCompanyFromImportProcessDto = {
      contactImportProcessId:
        contactImportProcess.contactImportProcessId,
      updateExternalCompanyImports: [
        {
          externalCompanyImportId: selectedCompany.externalCompanyImportId,
          legalName: values.legalName,
          displayName: values.displayName,
          notes: values.notes || '',
          commercialRegisterNumber: values.commercialRegisterNumber || '',
          commercialRegisterCity: values.commercialRegisterCity || '',
          legalForm: values.legalForm || '',
          vatNumber: values.vatNumber || '',
          existingCompanyId: selectedCompany.existingCompanyId,
          description: selectedCompany.description || '',
          action: values.action,
        },
      ],
    };

    try {
      const result = await apiUpdateContactsFromImportProcess(
        contactImportProcess.contactImportProcessId,
        updateDto
      );

      if (result.result.ok) {
        const updatedProcess = {
          ...contactImportProcess,
          externalCompanyImports:
            (contactImportProcess.externalCompanyImports ?? []).map((c) =>
              c.externalCompanyImportId ===
              selectedCompany.externalCompanyImportId
                ? {
                    ...c,
                    ...values,
                    externalBankAccountImports: bankAccounts,
                  }
                : c
            ),
        };
        onProcessUpdate(updatedProcess);
        onClose();
      }
    } catch (error) {
      console.error('Update failed:', error);
    }
  };

  const bankAccountColumns = [
    {
      title: t('settings:companyImport.table.iban'),
      dataIndex: 'iban',
      key: 'iban',
    },
    {
      title: t('settings:companyImport.table.bic'),
      dataIndex: 'bic',
      key: 'bic',
    },
    {
      title: t('settings:companyImport.table.creditInstitutionName'),
      dataIndex: 'creditInstitutionName',
      key: 'creditInstitutionName',
    },
    {
      title: t('settings:companyImport.table.isPrimary'),
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      render: (value: boolean) => (value ? 'Ja' : 'Nein'),
    },
  ];

  const renderCompanyTab = () => (
    <Form form={form} layout="vertical" onFinish={handleFinish}>
      <Form.Item
        name="legalName"
        label={t('settings:companyImport.table.legalName')}
        rules={[{ required: true, message: t('common:validation.required') }]}
        className={classes.formItem}
      >
        <Input full />
      </Form.Item>

      <Form.Item
        name="displayName"
        label={t('settings:companyImport.table.displayName')}
        className={classes.formItem}
      >
        <Input full />
      </Form.Item>

      <Form.Item
        name="notes"
        label={t('settings:companyImport.table.notes')}
        className={classes.formItem}
      >
        <TextArea rows={2} />
      </Form.Item>

      <Form.Item
        name="commercialRegisterNumber"
        label={t('settings:companyImport.table.commercialRegisterNumber')}
        className={classes.formItem}
      >
        <Input full />
      </Form.Item>

      <Form.Item
        name="commercialRegisterCity"
        label={t('settings:companyImport.table.commercialRegisterCity')}
        className={classes.formItem}
      >
        <Input full />
      </Form.Item>

      <Form.Item
        name="legalForm"
        label={t('settings:companyImport.table.legalForm')}
        className={classes.formItem}
      >
        <Input full />
      </Form.Item>

      <Form.Item
        name="vatNumber"
        label={t('settings:companyImport.table.vatNumber')}
        className={classes.formItem}
      >
        <Input full />
      </Form.Item>

      <Form.Item
        name="action"
        label={t('settings:companyImport.table.action')}
        className={classes.formItem}
      >
        <Select options={contactImportActionOptions(t)} />
      </Form.Item>

      <div className={classes.sectionTitle}>
        {t('settings:companyImport.table.bankAccounts')}
      </div>
      <Table
        dataSource={bankAccounts}
        columns={bankAccountColumns}
        rowKey="externalBankAccountImportId"
        pagination={false}
        size="small"
        className={classes.inlineTable}
      />

      <div className={classes.buttonContainer}>
        <Button type="primary" htmlType="submit">
          {t('common:actions.save')}
        </Button>
        <Button style={{ marginLeft: 8 }} onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>
      </div>
    </Form>
  );

  const renderBusinessUnitTab = () => (
    <div>
      {businessUnits.map((bu) => (
        <div
          key={bu.externalBusinessUnitImportId}
          style={{ marginBottom: 24, border: '1px solid #e8e8e8', padding: 16, borderRadius: 4 }}
        >
          <h4>
            {bu.displayName} {bu.isPrimary && '(Primary)'}
          </h4>

          {bu.emailAddresses.length > 0 && (
            <>
              <div className={classes.sectionTitle}>
                {t('settings:companyImport.table.emailAddresses')}
              </div>
              <Table
                dataSource={bu.emailAddresses}
                columns={[
                  {
                    title: t('settings:companyImport.table.email'),
                    dataIndex: 'address',
                    key: 'address',
                  },
                  {
                    title: t('settings:companyImport.table.isPrimary'),
                    dataIndex: 'isPrimary',
                    key: 'isPrimary',
                    render: (v: boolean) => (v ? 'Ja' : 'Nein'),
                  },
                ]}
                rowKey="externalEmailAddressImportId"
                pagination={false}
                size="small"
              />
            </>
          )}

          {bu.phoneNumbers.length > 0 && (
            <>
              <div className={classes.sectionTitle}>
                {t('settings:companyImport.table.phoneNumbers')}
              </div>
              <Table
                dataSource={bu.phoneNumbers}
                columns={[
                  {
                    title: t('settings:companyImport.table.number'),
                    dataIndex: 'number',
                    key: 'number',
                  },
                  {
                    title: t('settings:companyImport.table.phoneType'),
                    dataIndex: 'phoneType',
                    key: 'phoneType',
                    render: (v: number) =>
                      t(`settings:companyImport.phoneType.${v}`),
                  },
                  {
                    title: t('settings:companyImport.table.isPrimary'),
                    dataIndex: 'isPrimary',
                    key: 'isPrimary',
                    render: (v: boolean) => (v ? 'Ja' : 'Nein'),
                  },
                ]}
                rowKey="externalPhoneNumberImportId"
                pagination={false}
                size="small"
              />
            </>
          )}

          {bu.addresses.length > 0 && (
            <>
              <div className={classes.sectionTitle}>
                {t('settings:companyImport.table.addresses')}
              </div>
              <Table
                dataSource={bu.addresses}
                columns={[
                  {
                    title: t('settings:companyImport.table.street'),
                    dataIndex: 'street',
                    key: 'street',
                  },
                  {
                    title: t('settings:companyImport.table.zip'),
                    dataIndex: 'zip',
                    key: 'zip',
                  },
                  {
                    title: t('settings:companyImport.table.city'),
                    dataIndex: 'city',
                    key: 'city',
                  },
                  {
                    title: t('settings:companyImport.table.country'),
                    dataIndex: 'country',
                    key: 'country',
                  },
                ]}
                rowKey="externalAddressImportId"
                pagination={false}
                size="small"
              />
            </>
          )}
        </div>
      ))}
      {businessUnits.length === 0 && (
        <p>{t('settings:companyImport.noBusinessUnits')}</p>
      )}
    </div>
  );

  const renderContactsTab = () => (
    <div>
      <List
        itemLayout="horizontal"
        dataSource={contacts}
        renderItem={(contact: ExternalContactImportDto) => (
          <List.Item
            onClick={() => {
              setSelectedContact(contact);
              setIsContactDrawerOpen(true);
            }}
            style={{ cursor: 'pointer' }}
          >
            <List.Item.Meta
              title={`${contact.firstName} ${contact.lastName}`}
              description={`${contact.emailAddresses?.find((e) => e.isPrimary)?.address || contact.emailAddresses?.[0]?.address || ''} ${contact.department ? `| ${contact.department}` : ''} ${contact.position ? `| ${contact.position}` : ''}`}
            />
          </List.Item>
        )}
      />
      {selectedContact && (
        <ExternalContactDrawer
          visible={isContactDrawerOpen}
          onClose={() => {
            setIsContactDrawerOpen(false);
            setSelectedContact(null);
          }}
          contact={selectedContact}
          contactImportProcess={contactImportProcess}
          onProcessUpdate={onProcessUpdate}
        />
      )}
    </div>
  );

  const renderTabs = () => [
    {
      label: t('settings:contactImporter.tabs.company'),
      key: 'company',
      content: renderCompanyTab(),
    },
    {
      label: t('settings:contactImporter.tabs.businessUnits'),
      key: 'businessUnits',
      content: renderBusinessUnitTab(),
    },
    {
      label: t('settings:contactImporter.tabs.contacts'),
      key: 'contacts',
      content: renderContactsTab(),
    },
  ];

  return (
    <Drawer
      title={
        selectedCompany?.legalName ||
        t('settings:companyImport.drawer.title')
      }
      onClose={onClose}
      visible={visible}
      width={'default'}
    >
      <Tabs
        contentClassName="fullHeightContactImporter"
        defaultTabKey={activeKey}
        onChange={(key) => setActiveKey(key)}
        items={renderTabs()}
      />
    </Drawer>
  );
};

export default ExternalCompanyDrawer;
